import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Alert,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Autocomplete,
  Box,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Trash } from 'lucide-react';
import Loading from '../../../../components/loading';
import { useParams } from 'react-router-dom';
import MedicineFormModal from '../../../../register/medicines/medicineFormModal';
import { useSavePrescription } from '../hooks/useSavePrescription';
import { useProfessionals } from '../../../../hooks/useProfessionals';
import { useMedicines } from '../../../../hooks/useMedicines';

export default function PrescriptionModalWeb({ open, onClose, onSave }) {
  const { patientId } = useParams();

  const { professionals: dentists, isLoading: isDentistsLoading } = useProfessionals();
  const { medicines, isLoading: isMedicinesLoading, refetch: refetchMedicines } = useMedicines();
  const isLoading = isDentistsLoading || isMedicinesLoading;
  const { save, isSaving } = useSavePrescription();

  const [selectedDentistId, setSelectedDentistId] = useState('');
  const [isMedicineFormModalOpen, setMedicineFormModalOpen] = useState(false);

  const [medicinesToSave, setMedicinesToSave] = useState([]);
  const [date, setDate] = useState(dayjs());
  const [medicineToAdd, setMedicineToAdd] = useState('');
  const [medicineControlled, setMedicineControlled] = useState(false);
  const [quantityToAdd, setQuantityToAdd] = useState('');
  const [dosageToAdd, setDosageToAdd] = useState('');
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    const dentistIdFromUser = sessionStorage.getItem('dentistIdFromUser');
    if (dentistIdFromUser && dentistIdFromUser !== 'null' && dentistIdFromUser !== 'undefined') {
      setSelectedDentistId(dentistIdFromUser);
    }
  }, [open]);

  const savePrescription = () => {
    if (!selectedDentistId || medicinesToSave.length === 0) {
      setModalError(
        'É obrigatório informar profissional, data e adicionar pelo menos um medicamento.',
      );
      return;
    }

    const prescription = {
      dentistaId: selectedDentistId,
      pacienteId: patientId,
      data: date,
      medicamentos: medicinesToSave,
    };

    save(prescription)
      .then((returnFromApi) => {
        cleanFields();
        onSave(returnFromApi);
      })
      .catch((returnFromApi) => {
        setModalError(returnFromApi.response?.data);
        console.error(returnFromApi);
        onSave(returnFromApi);
      });
  };

  function adicionarMedicamentoNaLista() {
    let medicine = {
      nome: medicineToAdd,
      descricaoQuantidade: quantityToAdd,
      posologia: dosageToAdd,
      controlled: medicineControlled,
    };

    setMedicinesToSave((medicinesToSave) => [...medicinesToSave, medicine]);
    setMedicineToAdd('');
    setMedicineControlled(false);
    setQuantityToAdd('');
    setDosageToAdd('');
  }

  function removerMedicamentoDaLista(medicamento) {
    medicinesToSave.forEach(() => {
      const indexMedicamento = medicinesToSave.indexOf(medicamento);
      medicinesToSave.splice(indexMedicamento, 1);
    });
    setMedicinesToSave((medicinesToSave) => [...medicinesToSave]);
  }

  function cleanFields() {
    setMedicineToAdd('');
    setMedicineControlled(false);
    setQuantityToAdd('');
    setDosageToAdd('');
    setMedicinesToSave([]);
  }

  function handleNewMedicineSaved({ description }) {
    refetchMedicines();
    setMedicineToAdd(description);
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Loading isToHidden={isLoading === false && isSaving === false} />

      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Receituário
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Collapse in={!!modalError} sx={{ mt: 1, mb: 1 }}>
          <Alert severity="error" onClose={() => setModalError('')}>
            {modalError}
          </Alert>
        </Collapse>

        <Grid container spacing={2}>
          <Grid size={7}>
            <FormControl fullWidth>
              <InputLabel id="dentist-select-label">Profissional</InputLabel>
              <Select
                fullWidth
                labelId="dentist-combobox-label"
                id="dentist-combobox"
                value={selectedDentistId}
                label="Profissional"
                onChange={(e) => setSelectedDentistId(e.target.value)}
              >
                {dentists.length === 0 && (
                  <MenuItem disabled value="">
                    Nenhum profissional encontrado
                  </MenuItem>
                )}
                {dentists.map((dentist) => (
                  <MenuItem key={dentist.id} value={dentist.id}>
                    {dentist.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={5}>
            <DatePicker
              label="Data"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid size={9}>
            <Autocomplete
              freeSolo
              openOnFocus
              options={medicines}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
              }
              inputValue={medicineToAdd}
              onInputChange={(_, newValue) => {
                setMedicineToAdd(newValue);
                if (!newValue) setMedicineControlled(false);
              }}
              onChange={(_, newValue) => {
                if (newValue && typeof newValue !== 'string') {
                  setMedicineToAdd(newValue.description);
                  setDosageToAdd(newValue.dosage ?? '');
                  setMedicineControlled(newValue.controlled ?? false);
                }
              }}
              noOptionsText="Nenhum medicamento encontrado."
              PaperComponent={({ children, ...other }) => (
                <Paper {...other}>
                  {children}
                  <Box
                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Button fullWidth size="small" onClick={() => setMedicineFormModalOpen(true)}>
                      + Cadastrar novo medicamento
                    </Button>
                  </Box>
                </Paper>
              )}
              renderInput={(params) => <TextField {...params} fullWidth label="Medicamento" />}
            />
          </Grid>
          <Grid size={3}>
            <TextField
              fullWidth
              label="Quantidade"
              value={quantityToAdd}
              onChange={(e) => setQuantityToAdd(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Posologia"
              multiline
              rows={2}
              value={dosageToAdd}
              onChange={(e) => setDosageToAdd(e.target.value)}
            />
          </Grid>
        </Grid>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => adicionarMedicamentoNaLista()} variant="outlined">
            Adicionar
          </Button>
        </DialogActions>

        <DialogContent dividers>
          {medicinesToSave.length === 0 && (
            <Typography align="center" sx={{ py: 4 }}>
              Nenhum medicamento adicionado ainda.
            </Typography>
          )}
          <List disablePadding>
            {medicinesToSave.map((medicine, index) => (
              <React.Fragment key={index}>
                <ListItem
                  disableGutters
                  alignItems="flex-start"
                  secondaryAction={
                    <IconButton
                      aria-label="Deletar"
                      color="error"
                      onClick={() => removerMedicamentoDaLista(medicine)}
                    >
                      <Trash size={18} />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {' '}
                        {medicine.nome}{' '}
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        {' '}
                        <Typography variant="body2" color="text.primary">
                          {' '}
                          {medicine.descricaoQuantidade}
                        </Typography>
                        <Typography variant="body2">{medicine.posologia}</Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={savePrescription} variant="contained">
          Salvar
        </Button>
      </DialogActions>

      <MedicineFormModal
        open={isMedicineFormModalOpen}
        onClose={() => setMedicineFormModalOpen(false)}
        onSave={handleNewMedicineSaved}
      />
    </Dialog>
  );
}

PrescriptionModalWeb.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
